import JSZip from "jszip";

export interface DfuUpdate {
  kind: string;
  initPacket: Uint8Array;
  firmwareImage: Uint8Array;
}

interface ManifestEntry {
  bin_file: string;
  dat_file: string;
}

interface ManifestJson {
  manifest: Record<string, ManifestEntry>;
}

export async function parseDfuZip(zipBytes: Uint8Array | ArrayBuffer): Promise<DfuUpdate[]> {
  const zip = await new JSZip().loadAsync(zipBytes);

  const manifestFile = zip.file("manifest.json");
  if (!manifestFile) {
    throw new Error("DFU zip is missing manifest.json");
  }
  const manifestText = await manifestFile.async("text");
  let parsed: ManifestJson;
  try {
    parsed = JSON.parse(manifestText) as ManifestJson;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`manifest.json is not valid JSON: ${msg}`);
  }
  if (!parsed.manifest || typeof parsed.manifest !== "object") {
    throw new Error("manifest.json missing top-level 'manifest' object");
  }

  const entries = Object.entries(parsed.manifest);
  if (entries.length === 0) {
    throw new Error("manifest.json has no update entries");
  }

  const updates: DfuUpdate[] = [];
  for (const [kind, entry] of entries) {
    if (!entry || typeof entry.bin_file !== "string" || typeof entry.dat_file !== "string") {
      throw new Error(`manifest entry '${kind}' missing bin_file/dat_file`);
    }
    const datFile = zip.file(entry.dat_file);
    const binFile = zip.file(entry.bin_file);
    if (!datFile) throw new Error(`init packet '${entry.dat_file}' missing from zip`);
    if (!binFile) throw new Error(`firmware image '${entry.bin_file}' missing from zip`);

    const [initPacket, firmwareImage] = await Promise.all([
      datFile.async("uint8array"),
      binFile.async("uint8array"),
    ]);
    updates.push({ kind, initPacket, firmwareImage });
  }

  return updates;
}
